import {
  Button,
  Card,
  IconButton,
  TableBody,
  TableCell,
  TableRow
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import { BannerDetails_banner_couponCollection } from "@saleor/banners/types/BannerDetails";
import CardTitle from "@saleor/components/CardTitle/CardTitle";
import CustomTooltip from "@saleor/components/CustomTooltip";
import ResponsiveTable from "@saleor/components/ResponsiveTable/ResponsiveTable";
import Skeleton from "@saleor/components/Skeleton";
import TableHead from "@saleor/components/TableHead";
import { COUPON_COLLECTION_TYPE_CHOICES } from "@saleor/coupons/constants";
import { maybe } from "@saleor/misc";
import moment from "moment";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

export interface BannerCouponCollectionsProps {
  couponCollection: BannerDetails_banner_couponCollection | null;
  onCouponCollectionAssign?: () => void;
  onCouponCollectionUnassign?: () => void;
  disabled?: boolean;
}

const useStyles = makeStyles(
  theme => ({
    buttonDisabled: {
      "& > span": {
        color: "rgba(24, 72, 254, 0.70)"
      }
    },
    colActions: {
      "&:last-child": {
        paddingRight: 0
      },
      width: 76 + theme.spacing(0.5)
    },
    deleteIcon: {
      color: "#AEA9B1"
    },
    colName: {
      paddingLeft: 0,
      width: 230
    },
    colNameLabel: {
      fontWeight: 600
    },
    colType: {
      width: 150
    },
    colAvailability: {
      width: 280
    },
    headerLabel: {
      fontWeight: 600
    },
    table: {
      tableLayout: "fixed"
    },
    tableRow: {
      cursor: "pointer"
    }
  }),
  { name: "BannerCouponCollections" }
);

const numberOfColumns = 4;

function formatAvailabilityDates(
  startDate: unknown,
  endDate: unknown
): React.ReactNode {
  const dateFmt = "MMMM D, YYYY";
  const start = startDate ? moment(startDate).format(dateFmt) : null;
  const end = endDate ? moment(endDate).format(dateFmt) : null;
  if (!start && !end) {
    return "—";
  }
  if (start && end) {
    return (
      <>
        Starts on <strong>{start}</strong> until <strong>{end}</strong>
      </>
    );
  }
  if (start) {
    return (
      <>
        Starts on <strong>{start}</strong>
      </>
    );
  }
  return (
    <>
      Until <strong>{end}</strong>
    </>
  );
}

function collectionTypeLabel(collectionType: string | null): string {
  if (!collectionType) {
    return "—";
  }
  const choice = COUPON_COLLECTION_TYPE_CHOICES.find(
    c => c.value === collectionType
  );
  return choice ? choice.label : collectionType;
}

export const BannerCouponCollections: React.FC<BannerCouponCollectionsProps> = props => {
  const {
    couponCollection,
    onCouponCollectionAssign,
    onCouponCollectionUnassign,
    disabled
  } = props;
  const classes = useStyles(props);
  const intl = useIntl();
  const hasAssignedCouponCollection = Boolean(couponCollection);
  const isAssignDisabled = hasAssignedCouponCollection || disabled;

  const assignCouponCollectionButton = (
    <Button
      id="assignCouponCollectionBtn"
      color="primary"
      disabled={isAssignDisabled}
      className={isAssignDisabled ? classes.buttonDisabled : null}
      onClick={onCouponCollectionAssign}
    >
      <FormattedMessage
        defaultMessage="Assign Coupon Collection"
        description="button"
      />
    </Button>
  );

  function showTable() {
    return (
      <ResponsiveTable className={classes.table}>
        <colgroup>
          <col className={classes.colName} />
          <col className={classes.colType} />
          <col className={classes.colAvailability} />
          <col className={classes.colActions} />
        </colgroup>
        <TableHead
          colSpan={numberOfColumns}
          disabled={false}
          hideCheckBox
          items={couponCollection ? [couponCollection] : []}
        >
          <TableCell className={classes.colName}>
            <span className={classes.colNameLabel}>
              <FormattedMessage
                defaultMessage="Coupon Collection Name"
                description="table column"
              />
            </span>
          </TableCell>
          <TableCell className={classes.colType}>
            <span className={classes.headerLabel}>
              <FormattedMessage
                defaultMessage="Type"
                description="table column — community or campaign"
              />
            </span>
          </TableCell>
          <TableCell className={classes.colAvailability}>
            <span className={classes.headerLabel}>
              <FormattedMessage
                defaultMessage="Availability Date(s)"
                description="table column — coupon collection start and end"
              />
            </span>
          </TableCell>
          <TableCell className={classes.colActions} />
        </TableHead>
        <TableBody>
          {couponCollection ? (
            <TableRow
              hover={!!couponCollection}
              key={couponCollection.id}
              className={classes.tableRow}
            >
              <TableCell className={classes.colName} id="couponCollectionTitle">
                {maybe<React.ReactNode>(
                  () => couponCollection.title,
                  <Skeleton />
                )}
              </TableCell>
              <TableCell className={classes.colType} id="couponCollectionType">
                {collectionTypeLabel(couponCollection.collectionType)}
              </TableCell>
              <TableCell
                className={classes.colAvailability}
                id="couponCollectionAvailability"
              >
                {formatAvailabilityDates(
                  couponCollection.startDate,
                  couponCollection.endDate
                )}
              </TableCell>
              <TableCell
                className={classes.colActions}
                id="couponCollectionDelete"
              >
                <IconButton
                  disabled={disabled}
                  onClick={e => {
                    e.stopPropagation();
                    onCouponCollectionUnassign?.();
                  }}
                >
                  <DeleteIcon className={classes.deleteIcon} />
                </IconButton>
              </TableCell>
            </TableRow>
          ) : (
            <TableRow>
              <TableCell colSpan={numberOfColumns}>
                <FormattedMessage defaultMessage="No coupon collection assigned yet" />
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </ResponsiveTable>
    );
  }

  return (
    <Card>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Coupon Collection",
          description: "section header"
        })}
        toolbar={
          hasAssignedCouponCollection ? (
            <CustomTooltip
              text="Remove the selected Coupon Collection to assign a new one."
              bgColor="#2B3039"
              width={300}
            >
              {assignCouponCollectionButton}
            </CustomTooltip>
          ) : (
            assignCouponCollectionButton
          )
        }
      />
      {showTable()}
    </Card>
  );
};

BannerCouponCollections.displayName = "BannerCouponCollections";
export default BannerCouponCollections;
